import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useEffect, useRef, useState } from 'react';

import { IOnlinePublicationMessages, IStoriesFolder, IStoryListSelection } from '../../../../../../types';

import { styles } from './MenuSidebar.styles';

export type ISidebarProps = {
  currentFolderId: string;
  isOrganizeStoriesInProgress: boolean;
  loadingMenuItems: boolean;
  menuItems: IStoriesFolder[];
  messages: IOnlinePublicationMessages;
  setIsAddMenuModalVisible(value: boolean): void;
  setIsReorderMenuActive(value: boolean): void;
  setSelectedMenu(section: IStoryListSelection): void;
};

type NodeToggleMap = { [nodeId: string]: boolean };

const renderMenuNodes = ({
  nodes,
  currentFolderId,
  toggledMap,
  onNodeToggle,
  onNodeSelect
}: {
  nodes: IStoriesFolder[];
  currentFolderId: string;
  toggledMap: NodeToggleMap;
  onNodeToggle: (node: IStoriesFolder) => void;
  onNodeSelect: (node: IStoriesFolder) => void;
}): React.ReactNode => {
  return nodes.map((node) => {
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = currentFolderId === node.id;

    if (hasChildren) {
      const isOpen = toggledMap[node.id] ?? false;
      return (
        <React.Fragment key={node.id}>
          <ListItemButton
            onClick={() => onNodeToggle(node)}
            sx={styles.sectionItem}
          >
            <ListItemText
              primary={
                <Typography variant="body2" sx={styles.sectionLabel}>
                  {node.displayName}
                </Typography>
              }
            />
            {isOpen ? (
              <ExpandLessIcon aria-hidden={true} fontSize="small" />
            ) : (
              <ExpandMoreIcon aria-hidden={true} fontSize="small" />
            )}
          </ListItemButton>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding sx={styles.nestedList}>
              {renderMenuNodes({
                nodes: node.children ?? [],
                currentFolderId,
                toggledMap,
                onNodeToggle,
                onNodeSelect
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItemButton
        key={node.id}
        selected={isSelected}
        onClick={() => onNodeSelect(node)}
        sx={styles.menuItem}
      >
        <ListItemText
          primary={
            <Typography variant="body2" sx={styles.menuItemLabel}>
              {node.displayName}
            </Typography>
          }
        />
      </ListItemButton>
    );
  });
};

export const Sidebar: React.FC<ISidebarProps> = (props) => {
  const {
    currentFolderId,
    isOrganizeStoriesInProgress,
    loadingMenuItems,
    menuItems,
    messages,
    setIsAddMenuModalVisible,
    setIsReorderMenuActive,
    setSelectedMenu
  } = props;

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [toggledMap, setToggledMap] = useState<NodeToggleMap>({});
  const [folderStructure, setFolderStructure] = useState<IStoriesFolder[]>([]);
  const prevMenuItemsRef = useRef<IStoriesFolder[] | null>(null);

  useEffect(() => {
    if (prevMenuItemsRef.current === menuItems) return;
    prevMenuItemsRef.current = menuItems;

    const applyToggleState = (node: IStoriesFolder): IStoriesFolder => ({
      ...node,
      toggled: toggledMap[node.id] ?? true,
      children: (node.children || []).map(applyToggleState)
    });
    setFolderStructure(menuItems.map(applyToggleState));
  }, [menuItems]);

  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const onNodeToggle = (node: IStoriesFolder) => {
    setToggledMap((prev) => ({ ...prev, [node.id]: !(prev[node.id] ?? false) }));
  };

  const onNodeSelect = (node: IStoriesFolder) => {
    if (node?.id && node?.displayName) {
      setSelectedMenu({ id: node.id, displayName: node.displayName });
    }
  };

  const dataLength = folderStructure.length;

  const renderMenuOptions = () => (
    <>
      <IconButton
        size="small"
        data-test="menu-dropdown"
        aria-label="Menu options"
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        aria-controls={isMenuOpen ? 'menu-sidebar-options' : undefined}
        disabled={isOrganizeStoriesInProgress}
        onClick={handleMenuOpen}
      >
        <MoreVertIcon aria-hidden={true} fontSize="small" />
      </IconButton>
      <Menu
        id="menu-sidebar-options"
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          data-test="dropdown-add-menu"
          onClick={() => {
            setIsAddMenuModalVisible(true);
            handleMenuClose();
          }}
        >
          {messages.addMenu}
        </MenuItem>
        <MenuItem
          data-test="dropdown-reorder"
          onClick={() => {
            setIsReorderMenuActive(true);
            handleMenuClose();
          }}
        >
          {messages.reorder}
        </MenuItem>
      </Menu>
    </>
  );

  const getDataView = () => {
    if (loadingMenuItems) {
      return (
        <Box sx={styles.loadingSpinner}>
          <CircularProgress
            size={24}
            role="status"
            aria-label="Loading menus"
            data-test="loading-menus-spinner"
          />
        </Box>
      );
    }

    if (!dataLength) {
      return (
        <Box sx={styles.noSectionsView} data-test="no-menu-view">
          <ListAltIcon aria-hidden={true} sx={styles.folderIcon} />
          <Typography variant="body2" sx={styles.noSectionsText}>
            {'No nav menus to show'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={styles.menuListContainer}>
        <List disablePadding data-test="menu-list">
          {renderMenuNodes({
            nodes: folderStructure,
            currentFolderId,
            toggledMap,
            onNodeToggle,
            onNodeSelect
          })}
        </List>
      </Box>
    );
  };

  return (
    <Box component="aside" sx={styles.sidebar}>
      <Box>
        <Box sx={styles.filterHeader}>
          <Box sx={styles.flexRow}>
            <FolderOpenIcon aria-hidden={true} sx={styles.folderIcon} />
            <Typography variant="subtitle1" sx={styles.foldersHeader}>
              {'Menu'}
            </Typography>
          </Box>
          {dataLength > 0 && renderMenuOptions()}
        </Box>
      </Box>
      <Divider aria-hidden={true} />
      {getDataView()}
    </Box>
  );
};
